import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Text,
  Group,
  Stack,
  ActionIcon,
  Modal,
  Button,
  useMantineTheme,
  useMantineColorScheme,
  Paper,
  Tooltip,
  ScrollArea,
  Select,
  Grid,
  Divider,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import { RunningMessage, DisplayRunningMessageInput } from "@/utils/api";
import { useRunningMessages } from "@/hooks/useRunningMessages";
import { LoadingIndicator, AppIcon } from "@/components/generic";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export interface DisplayRunningMessageSelectorProps {
  /** Display ID to filter running messages by (optional for new displays) */
  displayId?: string | null | undefined;

  /** Current running message assignments */
  value: DisplayRunningMessageInput[] | null | undefined;

  /** Callback when running message assignments change */
  onChange?: (assignments: DisplayRunningMessageInput[]) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to include inactive running messages */
  includeInactive?: boolean;

  /** Custom filter function for running messages */
  runningMessageFilter?: (runningMessage: RunningMessage) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Maximum height for the selected items area */
  maxHeight?: number;
}

interface RunningMessageAssignmentForm {
  runningMessageId: string;
  startDatetime?: string | null;
  endDatetime?: string | null;
  orderNo?: number | null;
}

export const DisplayRunningMessageSelector: React.FC<
  DisplayRunningMessageSelectorProps
> = ({
  displayId,
  value,
  onChange,
  placeholder = "No running message assignments",
  runningMessageFilter,
  loadingPlaceholder = "Loading running messages...",
  emptyText = "No running messages available to assign",
  error,
  disabled,
  size = "md",
  maxHeight = 400,
}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [assignmentModalOpened, setAssignmentModalOpened] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Use running messages hook
  const { runningMessages, loading, lastError } = useRunningMessages({
    autoLoad: true,
    pageSize: 100, // Load more items for selection
    initialFilters: {}, // Filter by active status
  });

  // Form for adding/editing assignments
  const assignmentForm = useForm<RunningMessageAssignmentForm>({
    initialValues: {
      runningMessageId: "",
      startDatetime: null,
      endDatetime: null,
      orderNo: undefined,
    },
    validate: {
      runningMessageId: (value) =>
        !value ? "Running message is required" : null,
      endDatetime: (value, values) => {
        if (value && values.startDatetime && value <= values.startDatetime) {
          return "End date must be after start date";
        }
        return null;
      },
    },
  });

  // Current assignments
  const currentAssignments = useMemo(() => {
    return value || [];
  }, [value]);

  // Filter and prepare running messages
  const availableRunningMessages = useMemo(() => {
    let filtered = runningMessages;

    // Apply custom filter if provided
    if (runningMessageFilter) {
      filtered = filtered.filter(runningMessageFilter);
    }

    return filtered;
  }, [runningMessages, runningMessageFilter]);

  // Get running message by ID for display
  const getRunningMessageById = useCallback(
    (id: string) => {
      return availableRunningMessages.find((msg) => msg.id === id);
    },
    [availableRunningMessages]
  );

  // Format date for display
  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return "Not set";
    try {
      return dayjs(dateString).format("DD MMM, YYYY @ hh:mm A");
    } catch {
      return "Invalid date";
    }
  };

  // Get status info for display
  // const getStatusInfo = (status?: number) => {
  //   switch (status) {
  //     case 100:
  //       return { label: "Active", color: "green" };
  //     case 0:
  //       return { label: "Inactive", color: "red" };
  //     default:
  //       return { label: "Unknown", color: "gray" };
  //   }
  // };

  // Handle opening add modal
  const handleOpenAddModal = useCallback(() => {
    assignmentForm.reset();
    // Set default order number
    const maxOrder = Math.max(
      0,
      ...currentAssignments.map((a) => a.orderNo || 0)
    );
    assignmentForm.setFieldValue("orderNo", maxOrder + 1);
    setEditingIndex(null);
    setAssignmentModalOpened(true);
  }, [currentAssignments, assignmentForm]);

  // Handle opening edit modal
  const handleOpenEditModal = useCallback(
    (index: number) => {
      const assignment = currentAssignments[index];
      if (assignment) {
        assignmentForm.setValues({
          runningMessageId: assignment.runningMessageId || "",
          startDatetime: assignment.startDatetime
            ? dayjs(assignment.startDatetime).format("YYYY-MM-DDTHH:mm:00Z")
            : null,
          endDatetime: assignment.endDatetime
            ? dayjs(assignment.endDatetime).format("YYYY-MM-DDTHH:mm:00Z")
            : null,
          orderNo: assignment.orderNo,
        });
        setEditingIndex(index);
        setAssignmentModalOpened(true);
      }
    },
    [currentAssignments, assignmentForm]
  );

  // Handle saving assignment
  const handleSaveAssignment = useCallback(() => {
    if (!assignmentForm.isValid()) {
      assignmentForm.validate();
      return;
    }

    const formValues = assignmentForm.values;
    const newAssignment: DisplayRunningMessageInput = {
      displayId,
      runningMessageId: formValues.runningMessageId,
      startDatetime: formValues.startDatetime
        ? dayjs(formValues.startDatetime).utc().format("YYYY-MM-DDTHH:mm:00Z")
        : null,
      endDatetime: formValues.endDatetime
        ? dayjs(formValues.endDatetime).utc().format("YYYY-MM-DDTHH:mm:00Z")
        : null,
      orderNo: formValues.orderNo,
    };

    let updatedAssignments: DisplayRunningMessageInput[];

    if (editingIndex !== null) {
      // Edit existing assignment
      updatedAssignments = [...currentAssignments];
      updatedAssignments[editingIndex] = newAssignment;
    } else {
      // Add new assignment
      updatedAssignments = [...currentAssignments, newAssignment];
    }

    onChange?.(updatedAssignments);
    setAssignmentModalOpened(false);
  }, [assignmentForm, currentAssignments, displayId, editingIndex, onChange]);

  // Handle removing assignment
  const handleRemoveAssignment = useCallback(
    (index: number) => {
      const updatedAssignments = currentAssignments.filter(
        (_, i) => i !== index
      );
      onChange?.(updatedAssignments);
    },
    [currentAssignments, onChange]
  );

  // Handle drag and drop reordering
  const handleDragEnd = useCallback(
    (result: any) => {
      if (!result.destination) return;

      const items = Array.from(currentAssignments);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      // Update order numbers
      const reorderedWithOrder = items.map((item, index) => ({
        ...item,
        orderNo: index + 1,
      }));

      onChange?.(reorderedWithOrder);
    },
    [currentAssignments, onChange]
  );

  // Create container styles
  const containerStyles = useMemo(
    () => ({
      minHeight: 80,
      maxHeight,
      borderRadius: theme.radius.sm,
      border: `1px solid ${error ? theme.colors.red[4] : theme.colors.gray[4]}`,
      backgroundColor:
        colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
    }),
    [theme, colorScheme, error, maxHeight]
  );

  // Show loading state in the main container
  if (loading && runningMessages.length === 0) {
    return (
      <>
        <Box style={containerStyles}>
          <Group gap="sm" p="md" wrap="nowrap">
            <LoadingIndicator size={size} color="gray" />
            <Text size={size} c="dimmed">
              {loadingPlaceholder}
            </Text>
          </Group>
        </Box>
        {error && (
          <Text size="sm" c="red" mt="xs">
            {error}
          </Text>
        )}
      </>
    );
  }

  // Show error state in the main container
  if (lastError && !loading && runningMessages.length === 0) {
    return (
      <>
        <Box style={containerStyles}>
          <Group gap="sm" p="md" wrap="nowrap">
            <AppIcon iconName="AlertCircle" size={16} color="red" />
            <Text size={size} c="red">
              Error loading running messages
            </Text>
          </Group>
        </Box>
        {error && (
          <Text size="sm" c="red" mt="xs">
            {error}
          </Text>
        )}
      </>
    );
  }

  // Main component render - always show the interface
  return (
    <>
      <Box style={containerStyles}>
        {/* Current assignments area */}
        {currentAssignments.length > 0 ? (
          <ScrollArea.Autosize mah={maxHeight - 60} p="sm">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="running-message-assignments">
                {(provided: any) => (
                  <Stack
                    gap="xs"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {currentAssignments.map((assignment, index) => {
                      const runningMessage = getRunningMessageById(
                        assignment.runningMessageId || ""
                      );
                      //const statusInfo = getStatusInfo(runningMessage?.status);

                      return (
                        <Draggable
                          key={`${assignment.runningMessageId}-${index}`}
                          draggableId={`assignment-${index}`}
                          index={index}
                          isDragDisabled={disabled ?? false}
                        >
                          {(provided: any, snapshot: any) => (
                            <Paper
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              p="sm"
                              withBorder
                              style={{
                                ...provided.draggableProps.style,
                                backgroundColor: snapshot.isDragging
                                  ? theme.colors.blue[0]
                                  : undefined,
                              }}
                            >
                              <Group
                                gap="xs"
                                wrap="nowrap"
                                style={{ alignItems: "flex-start" }}
                              >
                                <div {...provided.dragHandleProps}>
                                  <AppIcon
                                    iconName="GripVertical"
                                    size={16}
                                    style={{
                                      cursor: disabled ? "default" : "grab",
                                    }}
                                  />
                                </div>

                                <Stack gap={"xs"} flex={"1 1 0%"}>
                                  <Text
                                    size="sm"
                                    fw={500}
                                    truncate
                                    style={{ flex: 1 }}
                                  >
                                    {runningMessage?.name ||
                                      "Unknown Running Message"}
                                  </Text>

                                  <Group gap="md">
                                    <Text size="xs" c="dimmed">
                                      <strong>From:</strong>{" "}
                                      {formatDateTime(assignment.startDatetime)}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      <strong>To:</strong>{" "}
                                      {formatDateTime(assignment.endDatetime)}
                                    </Text>
                                  </Group>
                                </Stack>

                                <Group gap="xs">
                                  <Tooltip label="Edit">
                                    <ActionIcon
                                      size="sm"
                                      color="blue"
                                      variant="subtle"
                                      onClick={() => handleOpenEditModal(index)}
                                      disabled={disabled ?? false}
                                    >
                                      <AppIcon iconName="Edit" size={12} />
                                    </ActionIcon>
                                  </Tooltip>

                                  <Tooltip label="Remove">
                                    <ActionIcon
                                      size="sm"
                                      color="red"
                                      variant="subtle"
                                      onClick={() =>
                                        handleRemoveAssignment(index)
                                      }
                                      disabled={disabled ?? false}
                                    >
                                      <AppIcon iconName="X" size={12} />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Group>
                            </Paper>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          </ScrollArea.Autosize>
        ) : (
          <Group gap="sm" p="md" wrap="nowrap">
            {loading && runningMessages.length > 0 && (
              <LoadingIndicator size={size} color="gray" />
            )}
            <Text size={size} c="dimmed">
              {placeholder}
            </Text>
          </Group>
        )}

        {/* Add button - always show if not disabled and we have running messages available */}
        {!disabled && availableRunningMessages.length > 0 && (
          <Group
            justify="center"
            p="xs"
            style={{ borderTop: `1px solid ${theme.colors.gray[3]}` }}
          >
            <Button
              size="xs"
              variant="light"
              leftSection={<AppIcon iconName="Plus" size={14} />}
              onClick={handleOpenAddModal}
            >
              Add Running Message Assignment
            </Button>
          </Group>
        )}

        {/* Show message when no running messages are available */}
        {!loading && availableRunningMessages.length === 0 && (
          <Group
            justify="center"
            p="xs"
            style={{ borderTop: `1px solid ${theme.colors.gray[3]}` }}
          >
            <Text size="xs" c="dimmed">
              {emptyText}
            </Text>
          </Group>
        )}
      </Box>

      {/* Error display */}
      {error && (
        <Text size="sm" c="red" mt="xs">
          {error}
        </Text>
      )}

      {/* Add/Edit assignment modal */}
      <Modal
        opened={assignmentModalOpened}
        onClose={() => setAssignmentModalOpened(false)}
        title={
          editingIndex !== null
            ? "Edit Running Message Assignment"
            : "Add Running Message Assignment"
        }
        size="lg"
        centered
      >
        <Stack gap="md">
          <Select
            label="Running Message"
            placeholder="Select a running message"
            required
            data={availableRunningMessages.map((msg) => ({
              value: msg.id || "",
              label: msg.name || "Untitled",
            }))}
            searchable
            {...assignmentForm.getInputProps("runningMessageId")}
          />

          <Grid>
            <Grid.Col span={6}>
              <DateTimePicker
                label="Start Date & Time"
                placeholder="Select start date and time"
                clearable
                {...assignmentForm.getInputProps("startDatetime")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <DateTimePicker
                label="End Date & Time"
                placeholder="Select end date and time"
                clearable
                {...assignmentForm.getInputProps("endDatetime")}
              />
            </Grid.Col>
          </Grid>

          <Text size="xs" c="dimmed">
            Leave dates empty for always-active assignment. End date must be
            after start date if both are set.
          </Text>

          <Divider />

          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => setAssignmentModalOpened(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAssignment}
              loading={
                assignmentForm.values.runningMessageId ? false : undefined
              }
            >
              {editingIndex !== null ? "Update" : "Add"} Assignment
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
